import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { enquiriesAPI } from '../../utils/api';
import '../../styles/AdminTable.scss';

const AdminCompletedEnquiries = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('mba'); // 'mba' or 'software'
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCompletedEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    filterEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiries, searchQuery]);

  const fetchCompletedEnquiries = async () => {
    try {
      setLoading(true);
      setError('');

      const response = activeTab === 'mba'
        ? await enquiriesAPI.getCompletedMba()
        : await enquiriesAPI.getCompletedSoftware();

      const responseData = response.data || response;
      const enquiriesArray = Array.isArray(responseData.data) ? responseData.data : [];
      setEnquiries(enquiriesArray);
    } catch (err) {
      setError('Failed to load completed enquiries');
      console.error('Error fetching completed enquiries:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEnquiry = async (id) => {
    if (window.confirm('Are you sure you want to permanently delete this enquiry? This action cannot be undone.')) {
      try {
        setLoading(true);
        setError('');
        await enquiriesAPI.delete(id);
        setEnquiries(prev => prev.filter(item => item.id !== id));
      } catch (err) {
        setError('Failed to delete enquiry');
        console.error('Error deleting enquiry:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const filterEnquiries = () => {
    let result = [...enquiries];

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        e =>
          (e.name && e.name.toLowerCase().includes(query)) ||
          (e.email && e.email.toLowerCase().includes(query)) ||
          (e.mobile && e.mobile.includes(query)) ||
          (e.projectTitle && e.projectTitle.toLowerCase().includes(query)) ||
          (e.specialization && e.specialization.toLowerCase().includes(query))
      );
    }

    setFilteredEnquiries(result);
  };

  const formatSpecialization = (spec) => {
    if (!spec) return '';
    return spec.replace(/^(MBA - |Software - )/, '');
  };

  const exportCsv = () => {
    if (!filteredEnquiries || filteredEnquiries.length === 0) return;

    const headers = [
      'Date Created',
      'Name',
      'Email',
      'Mobile',
      'Project Title',
      'Specialization',
      'Date Completed'
    ];

    const rows = filteredEnquiries.map(r => [
      new Date(r.createdAt || r.created_at).toLocaleDateString(),
      `"${(r.name ?? '').replace(/"/g, '""')}"`,
      `"${(r.email ?? '').replace(/"/g, '""')}"`,
      `"${(r.mobile ?? '').replace(/"/g, '""')}"`,
      `"${(r.projectTitle ?? '').replace(/"/g, '""')}"`,
      `"${formatSpecialization(r.specialization).replace(/"/g, '""')}"`,
      r.completedAt ? new Date(r.completedAt).toLocaleDateString() : 'N/A'
    ].join(','));

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `completed_${activeTab}_enquiries.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="admin-page admin-table-styles">
      <div className="page-header">
        <h1>Completed Enquiries Archive</h1>
        <p>View history and communication logs for all fully processed and resolved student leads.</p>
      </div>

      {/* Tabs Menu */}
      <div
        className="tabs-container"
        style={{
          display: 'flex',
          borderBottom: '2px solid #e2e8f0',
          marginBottom: '24px',
          gap: '24px',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        <button
          onClick={() => setActiveTab('mba')}
          style={{
            padding: '12px 8px',
            fontSize: '16px',
            fontWeight: 700,
            color: activeTab === 'mba' ? '#4318ff' : '#64748b',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'mba' ? '3px solid #4318ff' : '3px solid transparent',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginBottom: '-2px',
            whiteSpace: 'nowrap'
          }}
        >
          🎓 MBA Completed ({activeTab === 'mba' ? filteredEnquiries.length : 'MBA'})
        </button>
        <button
          onClick={() => setActiveTab('software')}
          style={{
            padding: '12px 8px',
            fontSize: '16px',
            fontWeight: 700,
            color: activeTab === 'software' ? '#4318ff' : '#64748b',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'software' ? '3px solid #4318ff' : '3px solid transparent',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginBottom: '-2px',
            whiteSpace: 'nowrap'
          }}
        >
          💻 Software Completed ({activeTab === 'software' ? filteredEnquiries.length : 'Software'})
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Search and Action Bar */}
      <div
        className="admin-actions-bar"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '15px',
          marginBottom: '20px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}
      >
        <div className="filters-group" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', width: '100%', maxWidth: '320px' }}>
          <input
            type="text"
            placeholder={`Search completed ${activeTab === 'mba' ? 'MBA' : 'Software'} enquiries...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '10px 16px',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              width: '100%',
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => e.target.style.borderColor = '#4318ff'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>

        <div className="buttons-group" style={{ display: 'flex', gap: '10px' }}>
          <button
            className="btn-primary"
            onClick={fetchCompletedEnquiries}
            disabled={loading}
            style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {loading ? 'Refreshing...' : '🔄 Refresh'}
          </button>
          <button
            className="btn-primary"
            onClick={exportCsv}
            disabled={filteredEnquiries.length === 0}
            style={{ padding: '10px 20px', background: '#475569' }}
          >
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="content-wrapper">
        <div className="table-container" style={{ width: '100%' }}>
          {loading && enquiries.length === 0 ? (
            <p style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>Loading completed archive...</p>
          ) : filteredEnquiries.length === 0 ? (
            <p style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>No completed enquiries found</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date Created</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Project Title</th>
                  <th>Specialization</th>
                  <th>Date Completed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEnquiries.map((enquiry) => (
                  <tr key={enquiry.id}>
                    <td>{new Date(enquiry.createdAt || enquiry.created_at).toLocaleDateString()}</td>
                    <td style={{ fontWeight: 600 }}>{enquiry.name}</td>
                    <td>
                      <a href={`mailto:${enquiry.email}`}>{enquiry.email}</a>
                    </td>
                    <td>
                      <a href={`tel:${enquiry.mobile}`}>{enquiry.mobile}</a>
                    </td>
                    <td
                      style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={enquiry.projectTitle}
                    >
                      {enquiry.projectTitle}
                    </td>
                    <td>{formatSpecialization(enquiry.specialization)}</td>
                    <td style={{ color: '#01b574', fontWeight: 600 }}>
                      {enquiry.completedAt ? new Date(enquiry.completedAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="actions">
                      <button
                        className="btn-view"
                        onClick={() => navigate(`/admin/${activeTab}-enquiries/${enquiry.id}`)}
                        title="View Details & Communication Logs"
                      >
                        View
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteEnquiry(enquiry.id)}
                        title="Delete Enquiry"
                        style={{
                          background: '#fff5f5',
                          color: '#e53e3e',
                          border: '1px solid #fed7d7',
                          padding: '8px 14px',
                          borderRadius: '20px',
                          fontWeight: '700',
                          fontSize: '13px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(229, 62, 62, 0.05)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#e53e3e';
                          e.target.style.color = 'white';
                          e.target.style.borderColor = '#e53e3e';
                          e.target.style.boxShadow = '0 4px 10px rgba(229, 62, 62, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#fff5f5';
                          e.target.style.color = '#e53e3e';
                          e.target.style.borderColor = '#fed7d7';
                          e.target.style.boxShadow = '0 2px 4px rgba(229, 62, 62, 0.05)';
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCompletedEnquiries;
